// CommissionTable.jsx

import React, { useEffect, useState } from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableRow,
    useMediaQuery,
    useTheme,
    Box,
    TableContainer,
    Container,
    TableHead,
    Typography,
    Tooltip,
} from "@mui/material";
import HoverPaper from "./shared/HoverPaper";
import Colours from "../helpers/colours";
import { months, months_abbreviations } from "../helpers/dates";
import { fns } from "../helpers/common";
import TableHeadSort from "./TableHeadSort";
import {
    DATATYPE_CHECKOUT,
    DATATYPE_RESDATE,
    DISPLAY_ELEMENT_HELPER,
    getMetricName,
} from "../helpers/filters";
import ToggleButtonTitled from "./ToggleButtonTitled";
import NoDataRow from "./NoDataRow";
import SmallPercentage from "./SmallPercentage";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import CheckShow from "./shared/CheckShow";
import { ARO_ADMIN } from "../configuration";

const headerStyles = {
    fontSize: "0.8em",
    fontWeight: "bold",
    backgroundColor: Colours.pillarBackgroundAttract,
    border: "1px solid lightgrey",
};

const formatMonthYear = (date) => {
    const year = date.getUTCFullYear();
    const month = date.getUTCMonth();
    const m_y = Number.parseInt(`${year}${month < 10 ? "0" + month : month}`);
    return m_y;
};

const parseMonthYear = (m_y) => {
    const year = Math.floor(m_y / 100);
    const month = m_y % 100;
    return new Date(Date.UTC(year, month));
};

const keyToLastYear = (key) => {
    if (key === "Total") return key;
    return Number(key) - 100; // 202400 → 202300

    // let date = parseMonthYear(key);
    // return formatMonthYear(
    //     new Date(date.getUTCFullYear() - 1, date.getUTCMonth(), 1)
    // );
};

const transformData = (
    datatype,
    js,
    data,
    selectedHotels,
    selectedCurrencies
) => {
    //const useOriginal = selectedCurrencies.length >= 1;
    const amountField = "AroTotal"; // useOriginal ? "OriginalCurrency" : "AroTotal"
    let filtered_data = [];

    if (selectedHotels.length === 0) {
        filtered_data = data;
    } else {
        filtered_data = data.filter((e) => selectedHotels.includes(e.SiteName));
    }
    if (selectedCurrencies.length === 0) {
        filtered_data = filtered_data;
    } else {
        filtered_data = filtered_data.filter((e) =>
            selectedCurrencies
                .map((c) => c.slice(0, 3))
                .includes(e.CurrencyCode)
        );
    }

    const { toDay, fromDay } = { fromDay: js.fromDay, toDay: js.toDay };

    let fromDayOneYearBefore = new Date();
    let toDayOneYearBefore = new Date();

    try {
        fromDayOneYearBefore = new Date(fromDay);
        fromDayOneYearBefore.setFullYear(
            fromDayOneYearBefore.getFullYear() - 1
        );
    } catch (_e) {
        console.log(_e);
    }

    try {
        toDayOneYearBefore = new Date(toDay);
        toDayOneYearBefore.setFullYear(toDayOneYearBefore.getFullYear() - 1);
    } catch (_e) {
        console.log(_e);
    }

    const filterData = (data, from, to) => {
        let transformedData = [];
        let totalMonths = {
            Total: {
                commissionNotCancelled: 0,
                commissionRoomsNotCancelled: 0,
                commissionVouchers: 0,
            },
        };

        data.filter(
            (e) =>
                new Date(`${e[datatype]}Z`) >= from &&
                new Date(`${e[datatype]}Z`) <= to &&
                e.CancelStatus === 0
        ).forEach((e) => {
            // const date_by = new Date(e[datatype]);
            const date = new Date(`${e[datatype]}Z`);
            const m_y = formatMonthYear(date);

            const siteName =
                `${e.SiteName}` +
                (!e.CurrencySymbol ? "" : ` (${e.CurrencySymbol})`); // +(!useOriginal && e.CurrencyCode !== "EUR" ? "*" : "");
            let key = e.HotelID ?? siteName;

            let client = transformedData.find((c) => c.HotelID === key);

            if (!client) {
                client = {
                    HotelID: key,
                    Client: siteName,
                    Total: {
                        commissionNotCancelled: 0,
                        commissionRoomsNotCancelled: 0,
                        commissionVouchers: 0,
                    }, // Initialize client total
                };
                transformedData.push(client);
            }

            if (!client[m_y]) {
                client[m_y] = {
                    commissionNotCancelled: 0,
                    commissionRoomsNotCancelled: 0,
                    commissionVouchers: 0,
                };
            }

            if (!totalMonths[m_y]) {
                totalMonths[m_y] = {
                    commissionNotCancelled: 0,
                    commissionRoomsNotCancelled: 0,
                    commissionVouchers: 0,
                };
            }

            const amount = e[amountField] ?? 0;

            // Update client month totals
            client[m_y].commissionNotCancelled += amount;
            totalMonths[m_y].commissionNotCancelled += amount;

            if (e.ResTypeID === 4) {
                client[m_y].commissionVouchers += amount;
                client.Total.commissionVouchers += amount; // Update client's total vouchers
                totalMonths[m_y].commissionVouchers += amount;
                totalMonths.Total.commissionVouchers += amount;
            } else {
                client[m_y].commissionRoomsNotCancelled += amount;
                client.Total.commissionRoomsNotCancelled += amount; // Update client's total rooms
                totalMonths[m_y].commissionRoomsNotCancelled += amount;
                totalMonths.Total.commissionRoomsNotCancelled += amount;
            }

            // Update client overall total
            client.Total.commissionNotCancelled += amount;
            totalMonths.Total.commissionNotCancelled += amount;
        });

        // Sort clients alphabetically
        transformedData.sort((a, b) => a.Client.localeCompare(b.Client));

        // Add overall totals row
        transformedData.push({
            Client: "Total",
            ...totalMonths,
        });

        return transformedData;
    };

    let dataToUse = filterData(
        filtered_data,
        new Date(fromDay),
        new Date(toDay)
    );
    let dataToUseLastYear = filterData(
        filtered_data,
        fromDayOneYearBefore,
        toDayOneYearBefore
    );

    return { dataToUse, dataToUseLastYear };
};

const CommissionTable = ({
    datatype = DATATYPE_CHECKOUT,
    js,
    data,
    selectedHotels,
    selectedCurrencies,
    metrics = {
        commissionNotCancelled: {
            ...DISPLAY_ELEMENT_HELPER.commissionTotal,
        },
        commissionRoomsNotCancelled: {
            ...DISPLAY_ELEMENT_HELPER.commissionRoomsTotal,
        },
        commissionVouchers: {
            ...DISPLAY_ELEMENT_HELPER.commissionVouchers,
        },
    },
    currencies = {},
}) => {
    const theme = useTheme();
    let tableOverflow = useMediaQuery(theme.breakpoints.down("lg"));

    const dataCommission = useSelector((state) => state.dataCommission);

    const [processedData, setProcessedData] = useState([]);
    const [processedDataLastYear, setProcessedDataLastYear] = useState([]);

    const [selectedData, setSelectedData] = useState([]);
    const [monthsToDisplay, setMonthsToDisplay] = useState([]);

    const [order, setOrder] = useState("asc");
    const [orderBy, setOrderBy] = useState("Client");

    const [valueType, setValueType] = useState(Object.keys(metrics)[0] ?? "");

    const getSymbol = (code) => currencies[code] ?? code;

    useEffect(() => {
        setProcessedData([]);
        setProcessedDataLastYear([]);
        setMonthsToDisplay([]);
        setSelectedData([]);

        if (!data || data.length === 0 || !js) {
            return;
        }

        const { dataToUse, dataToUseLastYear } = transformData(
            datatype,
            js,
            data,
            selectedHotels,
            selectedCurrencies
        );

        if (dataToUse.length === 0) {
            return;
        }

        const allMonths = new Set();

        // Add "Total" as the first entry in the set
        allMonths.add("Total");

        // first day of from-month
        const start = new Date(js.fromDay);
        start.setUTCDate(1);

        // first day of to-month
        const end = new Date(js.toDay);
        end.setUTCDate(1);

        // walk month by month
        for (
            let d = new Date(start);
            d <= end;
            d.setUTCMonth(d.getUTCMonth() + 1)
        ) {
            allMonths.add(formatMonthYear(d)); // e.g. 202405
        }

        setOrderBy("Client");
        setMonthsToDisplay([...allMonths]);
        setProcessedDataLastYear(dataToUseLastYear);
        setProcessedData(dataToUse);
    }, [data, js, selectedHotels, selectedCurrencies]);

    useEffect(() => {
        if (processedData.length === 0) {
            setSelectedData([]);
            return;
        }

        // Merge last year and current year
        let auxThisDict = {};
        processedData.forEach((element) => {
            auxThisDict[element.Client] = element;
        });
        processedDataLastYear.forEach((element) => {
            if (element.Client in auxThisDict)
                auxThisDict[element.Client]["LastYear"] = element;
        });

        let auxSelected = Object.values(auxThisDict).filter(
            (item) => item.Client !== "Total"
        );

        // Sort
        auxSelected.sort((a, b) => {
            if (orderBy === "Client") {
                let aValue = a[orderBy] ? a[orderBy].toLowerCase() : "";
                let bValue = b[orderBy] ? b[orderBy].toLowerCase() : "";
                return order === "asc"
                    ? aValue.localeCompare(bValue)
                    : bValue.localeCompare(aValue);
            } else {
                let aValue = a[orderBy] ? a[orderBy][valueType] : 0;
                let bValue = b[orderBy] ? b[orderBy][valueType] : 0;
                return order === "asc" ? aValue - bValue : bValue - aValue;
            }
        });

        setSelectedData(auxSelected);
    }, [processedData, order, orderBy, valueType]);

    if (processedData.length === 0 || monthsToDisplay.length === 0) {
        return;
    }

    const totalRow = processedData.find((row) => row.Client === "Total");
    const totalRowLastYear = processedDataLastYear.find(
        (row) => row.Client === "Total"
    );

    //const showConversionNote = selectedCurrencies.length <= 0;

    const baseName = (clientLabel) => clientLabel.replace(/\s\([^)]+\)$/, "");

    const findHotelObj = (year, name) => {
        const yearsObj = dataCommission?.data ?? {};
        const yData = yearsObj?.[year]?.data ?? {};
        for (const key of Object.keys(yData)) {
            const obj = yData[key];
            if (!obj) continue;
            const nm = obj.HotelName ?? obj.SiteName;
            if (nm === name) return obj;
        }
        return null;
    };

    const parseISODateUTC = (s) => {
        if (!s) return null;
        const [y, m, d] = String(s)
            .split("-")
            .map((n) => parseInt(n, 10));
        if (!y || m == null || !d) return null;
        return new Date(Date.UTC(y, m - 1, d));
    };

    const getTypeCommissionLabel = (hotelObj, typeName, targetDate) => {
        if (!hotelObj) return { label: "—", from: null, to: null };

        const modeMap = hotelObj?.CommissionMode ?? {};
        const mode = modeMap?.[typeName] ?? "PERCENT";

        const arr = Array.isArray(hotelObj?.Percentages)
            ? hotelObj.Percentages.filter((p) => p.TypeName === typeName)
            : [];

        // If a target month is provided, resolve the active record for that month
        if (targetDate) {
            let chosen = null;

            // First pass: exact interval hit [FromDate, ToDate]
            for (const rec of arr) {
                const fd = parseISODateUTC(rec.FromDate);
                const td = parseISODateUTC(rec.ToDate);
                if (fd && td && fd <= targetDate && targetDate <= td) {
                    chosen = { ...rec, _fd: fd, _td: td };
                    break;
                }
            }
            // Fallback: latest record with FromDate <= targetDate
            if (!chosen && arr.length) {
                const candidates = arr
                    .map((r) => ({ r, fd: parseISODateUTC(r.FromDate) }))
                    .filter((o) => o.fd && o.fd <= targetDate)
                    .sort((a, b) => b.fd - a.fd);
                if (candidates.length) {
                    const r = candidates[0].r;
                    chosen = {
                        ...r,
                        _fd: candidates[0].fd,
                        _td: parseISODateUTC(r.ToDate),
                    };
                }
            }

            if (chosen) {
                const v = chosen.CommissionValue;
                const toStr =
                    chosen.ToDate === "2099-12-31" ? "current" : chosen.ToDate;
                if (mode === "PER_BOOKING") {
                    return {
                        label: `${fns(1, v, {})} € / booking`,
                        from: chosen.FromDate,
                        to: toStr,
                    };
                }
                return { label: `${v}%`, from: chosen.FromDate, to: toStr };
            }
        }

        // Original behavior for non-targeted calls (avoid "varies")
        const uniq = Array.from(new Set(arr.map((p) => p.CommissionValue)));
        if (uniq.length === 1) {
            const v = uniq[0];
            if (mode === "PER_BOOKING") {
                return {
                    label: `${fns(1, v, {})} € / booking`,
                    from: null,
                    to: null,
                };
            }
            return { label: `${v}%`, from: null, to: null };
        }
        // Do not return "varies" anymore
        return { label: "—", from: null, to: null };
    };

    const buildTooltip = (clientLabel, monthKey) => {
        if (monthKey === "Total") return null;

        const year = Number(String(monthKey).slice(0, 4));
        const monthIdx = Number(String(monthKey).slice(4, 6)); // 0..11
        const targetDate = new Date(Date.UTC(year, monthIdx, 1)); // first day of that month (UTC)

        const name = baseName(clientLabel);
        const hotelObj = findHotelObj(year, name);
        const section = hotelObj?.[datatype] ?? {};

        const bookings_rooms =
            ((datatype === DATATYPE_RESDATE
                ? section?.Bookings_Rooms_All
                : section?.Bookings_Rooms_NoCanc) ?? [])[monthIdx] ?? 0;

        const bookings_vouchers =
            ((datatype === DATATYPE_RESDATE
                ? section?.Bookings_Vouchers_All
                : section?.Bookings_Vouchers_NoCanc) ?? [])[monthIdx] ?? 0;

        const totalPrice_rooms =
            ((datatype === DATATYPE_RESDATE
                ? section?.TotalPrice_Rooms_All
                : section?.TotalPrice_Rooms_NoCanc) ?? [])[monthIdx] ?? 0;

        const totalPrice_vouchers =
            ((datatype === DATATYPE_RESDATE
                ? section?.TotalPrice_Vouchers_All
                : section?.TotalPrice_Vouchers_NoCanc) ?? [])[monthIdx] ?? 0;

        const roomsPct = getTypeCommissionLabel(hotelObj, "ROOMS", targetDate);
        const vouchersPct = getTypeCommissionLabel(
            hotelObj,
            "VOUCHERS",
            targetDate
        );

        return {
            roomsPct: roomsPct.label,
            vouchersPct: vouchersPct.label,
            roomsFrom: roomsPct.from,
            roomsTo: roomsPct.to,
            vouchersFrom: vouchersPct.from,
            vouchersTo: vouchersPct.to,
            bookings_rooms,
            bookings_vouchers,
            totalPrice_rooms,
            totalPrice_vouchers,
        };
    };

    return (
        <>
            <ToggleButtonTitled
                TITLE=""
                valueSet={valueType}
                values={Object.fromEntries(
                    Object.keys(metrics).map((valueKey) => [
                        valueKey,
                        getMetricName(valueKey, "CommissionScreen"),
                    ])
                )}
                onChange={(e, v) => setValueType(v)}
            />

            {/* Tabla principal */}
            <Box component={HoverPaper}>
                <TableContainer sx={{ maxHeight: "85em", overflowX: "auto" }}>
                    <Table stickyHeader>
                        {/* ===== PRIMERA CABECERA (meses) ===== */}
                        <TableHeadSort
                            stickyHeads={tableOverflow ? 2 : 0}
                            headers={[
                                {
                                    id: "Client",
                                    label: "Client",
                                    styles: {
                                        ...headerStyles,
                                        position: "sticky",
                                        left: 0,
                                        zIndex: 10,
                                    },
                                    colSpan: 1,
                                },
                                ...monthsToDisplay.map((month) => ({
                                    id: String(month),
                                    label:
                                        month === "Total"
                                            ? "Total"
                                            : `${
                                                  months_abbreviations[
                                                      parseInt(
                                                          String(month).slice(
                                                              4,
                                                              6
                                                          )
                                                      )
                                                  ]
                                              } ${String(month).slice(0, 4)}`,
                                    styles: {
                                        ...headerStyles,
                                        textAlign: "center",
                                    },
                                })),
                            ]}
                            order={order}
                            orderBy={orderBy}
                            setOrder={setOrder}
                            setOrderBy={setOrderBy}
                            colSpan={3}
                        />

                        <TableBody>
                            {/* ===== SEGUNDA FILA DE CABECERAS (sub‑columnas) ===== */}
                            {selectedData.length > 0 && (
                                <TableRow>
                                    {/* Celda hueca sobre “Client” */}
                                    <TableCell
                                        sx={{
                                            border: "1px solid lightgrey",
                                            position: "sticky",
                                            zIndex: 2,
                                            left: 0,
                                            backgroundColor: Colours.plainWhite,
                                        }}
                                    >
                                        &nbsp;
                                    </TableCell>

                                    {/* Bloque de sub‑títulos SOLO para la primera terna */}
                                    <>
                                        <TableCell
                                            align="center"
                                            sx={{
                                                border: "1px solid lightgrey",
                                            }}
                                        >
                                            <Typography variant="caption">
                                                Current period
                                            </Typography>
                                        </TableCell>
                                        <TableCell
                                            align="center"
                                            sx={{
                                                border: "1px solid lightgrey",
                                                backgroundColor:
                                                    Colours.notificationCard,
                                            }}
                                        >
                                            <Typography variant="caption">
                                                Same time last year
                                            </Typography>
                                        </TableCell>
                                        <TableCell
                                            align="center"
                                            sx={{
                                                border: "1px solid lightgrey",
                                                backgroundColor:
                                                    Colours.tableRowBackground,
                                            }}
                                        >
                                            <Typography variant="caption">
                                                Diff
                                            </Typography>
                                        </TableCell>
                                    </>

                                    {/* Para los meses restantes solo dejamos las celdas vacías */}
                                    {monthsToDisplay.slice(1).map((_, idx) => (
                                        <React.Fragment key={idx}>
                                            <TableCell
                                                sx={{
                                                    border: "1px solid lightgrey",
                                                }}
                                            />
                                            <TableCell
                                                sx={{
                                                    border: "1px solid lightgrey",
                                                    backgroundColor:
                                                        Colours.notificationCard,
                                                }}
                                            />
                                            <TableCell
                                                sx={{
                                                    border: "1px solid lightgrey",
                                                    backgroundColor:
                                                        Colours.tableRowBackground,
                                                }}
                                            />
                                        </React.Fragment>
                                    ))}
                                </TableRow>
                            )}

                            {/* ===== FILAS DE DATOS ===== */}
                            {selectedData.length > 0 ? (
                                selectedData.map((row, idx) => {
                                    const lastYearRow = row.LastYear || {};
                                    return (
                                        <TableRow key={idx}>
                                            {/* Cliente */}
                                            <TableCell
                                                sx={{
                                                    fontWeight: "bold",
                                                    border: "1px solid lightgrey",
                                                    position: "sticky",
                                                    zIndex: 2,
                                                    left: 0,
                                                    backgroundColor:
                                                        Colours.plainWhite,
                                                }}
                                            >
                                                {row.Client}
                                            </TableCell>

                                            {/* Valores por mes */}
                                            {monthsToDisplay.map((month, i) => {
                                                const currentVal =
                                                    row[month]?.[valueType] ||
                                                    0;
                                                const lastYearVal =
                                                    lastYearRow?.[
                                                        keyToLastYear(month)
                                                    ]?.[valueType] || 0;

                                                // Added: build tooltip payload once per cell
                                                const tt =
                                                    month !== "Total"
                                                        ? buildTooltip(
                                                              row.Client,
                                                              month
                                                          )
                                                        : null;

                                                return (
                                                    <React.Fragment key={i}>
                                                        {/* Actual */}
                                                        <TableCell
                                                            align="right"
                                                            sx={{
                                                                border: "1px solid lightgrey",
                                                            }}
                                                        >
                                                            {tt ? (
                                                                <CheckShow
                                                                    rolesThatCanSee={
                                                                        ARO_ADMIN
                                                                    }
                                                                    alternative={
                                                                        <span>
                                                                            {fns(
                                                                                1,
                                                                                Math.round(
                                                                                    currentVal
                                                                                ),
                                                                                {}
                                                                            )}
                                                                        </span>
                                                                    }
                                                                >
                                                                    <Tooltip
                                                                        arrow
                                                                        title={
                                                                            <Box
                                                                                sx={{
                                                                                    p: 0.5,
                                                                                }}
                                                                            >
                                                                                <Typography variant="caption">
                                                                                    Rooms:{" "}
                                                                                    <b>
                                                                                        {
                                                                                            tt.roomsPct
                                                                                        }
                                                                                    </b>
                                                                                </Typography>
                                                                                <br />
                                                                                {/* Added: show From/To for Rooms */}
                                                                                <Typography variant="caption">
                                                                                    Valid:{" "}
                                                                                    <b>
                                                                                        {tt.roomsFrom ||
                                                                                            "—"}{" "}
                                                                                        →{" "}
                                                                                        {tt.roomsTo ||
                                                                                            "—"}
                                                                                    </b>
                                                                                </Typography>

                                                                                <br />
                                                                                <Typography variant="caption">
                                                                                    Bookings
                                                                                    Rooms:{" "}
                                                                                    <b>
                                                                                        {
                                                                                            tt.bookings_rooms
                                                                                        }
                                                                                    </b>
                                                                                </Typography>
                                                                                <br />
                                                                                <Typography variant="caption">
                                                                                    Total
                                                                                    price
                                                                                    Rooms:{" "}
                                                                                    <b>
                                                                                        {fns(
                                                                                            1,
                                                                                            Math.round(
                                                                                                tt.totalPrice_rooms ||
                                                                                                    0
                                                                                            ),
                                                                                            {}
                                                                                        )}
                                                                                    </b>
                                                                                </Typography>

                                                                                <hr />

                                                                                <Typography variant="caption">
                                                                                    Vouchers:{" "}
                                                                                    <b>
                                                                                        {
                                                                                            tt.vouchersPct
                                                                                        }
                                                                                    </b>
                                                                                </Typography>
                                                                                <br />
                                                                                {/* Added: show From/To for Vouchers */}
                                                                                <Typography variant="caption">
                                                                                    Valid:{" "}
                                                                                    <b>
                                                                                        {tt.vouchersFrom ||
                                                                                            "—"}{" "}
                                                                                        →{" "}
                                                                                        {tt.vouchersTo ||
                                                                                            "—"}
                                                                                    </b>
                                                                                </Typography>
                                                                                <br />
                                                                                <Typography variant="caption">
                                                                                    Bookings
                                                                                    Vouchers:{" "}
                                                                                    <b>
                                                                                        {
                                                                                            tt.bookings_vouchers
                                                                                        }
                                                                                    </b>
                                                                                </Typography>
                                                                                <br />
                                                                                <Typography variant="caption">
                                                                                    Total
                                                                                    price
                                                                                    Vouchers:{" "}
                                                                                    <b>
                                                                                        {fns(
                                                                                            1,
                                                                                            Math.round(
                                                                                                tt.totalPrice_vouchers ||
                                                                                                    0
                                                                                            ),
                                                                                            {}
                                                                                        )}
                                                                                    </b>
                                                                                </Typography>
                                                                            </Box>
                                                                        }
                                                                    >
                                                                        <span>
                                                                            {fns(
                                                                                1,
                                                                                Math.round(
                                                                                    currentVal
                                                                                ),
                                                                                {}
                                                                            )}
                                                                        </span>
                                                                    </Tooltip>
                                                                </CheckShow>
                                                            ) : (
                                                                fns(
                                                                    1,
                                                                    Math.round(
                                                                        currentVal
                                                                    ),
                                                                    {}
                                                                )
                                                            )}
                                                        </TableCell>

                                                        {/* Año anterior */}
                                                        <TableCell
                                                            align="right"
                                                            sx={{
                                                                border: "1px solid lightgrey",
                                                                backgroundColor:
                                                                    Colours.notificationCard,
                                                            }}
                                                        >
                                                            {fns(
                                                                1,
                                                                Math.round(
                                                                    lastYearVal
                                                                ),
                                                                {}
                                                            )}
                                                        </TableCell>

                                                        {/* Diferencia */}
                                                        <TableCell
                                                            align="right"
                                                            sx={{
                                                                border: "1px solid lightgrey",
                                                                backgroundColor:
                                                                    Colours.tableRowBackground,
                                                            }}
                                                        >
                                                            <SmallPercentage
                                                                brackets={false}
                                                                tooltip={false}
                                                                bubble
                                                                bubbleRound
                                                                actual={
                                                                    currentVal
                                                                }
                                                                actualName="Actual"
                                                                oldName="Last year"
                                                                old={
                                                                    lastYearVal
                                                                }
                                                                showFormula={
                                                                    false
                                                                }
                                                            />
                                                        </TableCell>
                                                    </React.Fragment>
                                                );
                                            })}
                                        </TableRow>
                                    );
                                })
                            ) : (
                                <NoDataRow text="No data for these filters" />
                            )}
                        </TableBody>

                        {/* ===== FILA DE TOTALES STICKY ABAJO ===== */}
                        {totalRow && (
                            <TableHead
                                sx={{
                                    position: "sticky",
                                    bottom: 0,
                                    zIndex: 20,
                                }}
                            >
                                <TableRow>
                                    <TableCell
                                        sx={{
                                            fontWeight: "bold",
                                            border: "1px solid lightgrey",
                                            position: "sticky",
                                            left: 0,
                                            zIndex: 21,
                                            backgroundColor: Colours.plainWhite,
                                        }}
                                    >
                                        {totalRow.Client} {" (€)"}
                                    </TableCell>

                                    {monthsToDisplay.map((month, i) => {
                                        const currentVal =
                                            totalRow[month]?.[valueType] || 0;
                                        const lastYearVal =
                                            totalRowLastYear?.[
                                                keyToLastYear(month)
                                            ]?.[valueType] || 0;

                                        return (
                                            <React.Fragment key={i}>
                                                <TableCell
                                                    align="right"
                                                    sx={{
                                                        fontWeight: "bold",
                                                        border: "1px solid lightgrey",
                                                    }}
                                                >
                                                    {fns(
                                                        1,
                                                        Math.round(currentVal),
                                                        {}
                                                    )}
                                                </TableCell>
                                                <TableCell
                                                    align="right"
                                                    sx={{
                                                        fontWeight: "bold",
                                                        border: "1px solid lightgrey",
                                                        backgroundColor:
                                                            Colours.notificationCard,
                                                    }}
                                                >
                                                    {fns(
                                                        1,
                                                        Math.round(lastYearVal),
                                                        {}
                                                    )}
                                                </TableCell>
                                                <TableCell
                                                    align="right"
                                                    sx={{
                                                        fontWeight: "bold",
                                                        border: "1px solid lightgrey",
                                                        backgroundColor:
                                                            Colours.tableRowBackground,
                                                    }}
                                                >
                                                    <SmallPercentage
                                                        brackets={false}
                                                        tooltip={false}
                                                        bubble
                                                        bubbleRound
                                                        actual={currentVal}
                                                        actualName="Current"
                                                        oldName="Last year"
                                                        old={lastYearVal}
                                                        showFormula={false}
                                                    />
                                                </TableCell>
                                            </React.Fragment>
                                        );
                                    })}
                                </TableRow>
                            </TableHead>
                        )}
                    </Table>
                </TableContainer>
            </Box>

            {/* ===== NOTA DEL ASTERISCO FUERA DEL CONTENEDOR ===== */}
            {/*showConversionNote && (
                <Typography variant="caption" sx={{ display: "block", mt: 1 }}>
                    * Values converted to EUR from local currency if no currency
                    filter is applied
                </Typography>
            )*/}
        </>
    );
};

export default CommissionTable;
